import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileUp, Mail, Pencil, Phone, Plus, Search, Trash2, Truck } from 'lucide-react';
import { useDeferredValue, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/components/page-header';
import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Skeleton,
  Textarea,
  useToast,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import { useFeatures } from '@/lib/features';
import { ImportSuppliersDialog } from './import-suppliers-dialog';
import { SUPPLIERS_KEY, suppliersApi, type Supplier, type SupplierInput } from './suppliers-api';
import { useCanEdit } from '@/lib/auth';

/** ספקים: אנשי קשר, והחוזים מול כל ספק. */
export function SuppliersPage() {
  const canEdit = useCanEdit();
  const { t } = useTranslation(['suppliers', 'common']);
  const toast = useToast();
  const qc = useQueryClient();
  const { has } = useFeatures();
  const list = useQuery({ queryKey: SUPPLIERS_KEY, queryFn: ({ signal }) => suppliersApi.list(signal) });
  const [editing, setEditing] = useState<Supplier | 'new' | null>(null);
  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState('');
  const term = useDeferredValue(search.trim().toLowerCase());

  const remove = useMutation({
    mutationFn: (id: string) => suppliersApi.remove(id),
    onSuccess: async () => {
      toast.success(t('page.removed'));
      await qc.invalidateQueries({ queryKey: SUPPLIERS_KEY });
    },
    onError: (e) => toast.error(t('common:errors.removeFailed'), e instanceof ApiError ? e.message : undefined),
  });

  const items = (list.data ?? []).filter(
    (s) => !term || [s.name, s.contactName, s.phone, s.email].some((v) => v?.toLowerCase().includes(term)),
  );

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('page.title')}
        description={t('page.description')}
        action={
          canEdit ? (
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" onClick={() => setImporting(true)} data-track="supplier.import">
                <FileUp aria-hidden />
                {t('page.importFromFile')}
              </Button>
              <Button size="sm" onClick={() => setEditing('new')}>
                <Plus aria-hidden />
                {t('page.new')}
              </Button>
            </div>
          ) : undefined
        }
      />
      <Card>
        <div className="border-b border-border p-3">
          <div className="relative max-w-sm">
            <Search className="pointer-events-none absolute inset-y-0 start-3 my-auto size-4 text-fg-subtle" aria-hidden />
            <Input className="ps-9" placeholder={t('page.searchPlaceholder')} aria-label={t('page.searchLabel')} value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
        {list.isPending ? (
          <div className="space-y-2 p-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={Truck}
            title={term ? t('page.notFound') : t('page.empty')}
            description={term ? t('page.tryAnotherSearch') : t('page.emptyHint')}
            action={
              term || !canEdit ? undefined : (
                <div className="flex gap-2">
                  <Button size="sm" variant="secondary" onClick={() => setImporting(true)} data-track="supplier.import">
                    <FileUp aria-hidden />
                    {t('page.importFromFile')}
                  </Button>
                  <Button size="sm" onClick={() => setEditing('new')}>
                    <Plus aria-hidden />
                    {t('page.first')}
                  </Button>
                </div>
              )
            }
          />
        ) : (
          <ul className="divide-y divide-border">
            {items.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-fg">{s.name}</p>
                  <p className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-fg-muted">
                    {s.contactName ? <span>{s.contactName}</span> : null}
                    {s.phone ? (
                      <a href={`tel:${s.phone}`} className="inline-flex items-center gap-1 hover:text-accent" dir="ltr">
                        <Phone className="size-3" aria-hidden />
                        {s.phone}
                      </a>
                    ) : null}
                    {s.email ? (
                      <a href={`mailto:${s.email}`} className="inline-flex items-center gap-1 hover:text-accent" dir="ltr">
                        <Mail className="size-3" aria-hidden />
                        {s.email}
                      </a>
                    ) : null}
                  </p>
                </div>
                {has('contracts') && s._count?.contracts ? (
                  <Badge tone="neutral">{t('page.contracts', { count: s._count.contracts })}</Badge>
                ) : null}
                {canEdit ? (
                  <>
                    <Button variant="ghost" size="sm" aria-label={t('page.editAria', { name: s.name })} onClick={() => setEditing(s)}>
                      <Pencil aria-hidden />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('page.removeAria', { name: s.name })}
                      loading={remove.isPending && remove.variables === s.id}
                      onClick={() => {
                        if (window.confirm(t('page.confirmRemove', { name: s.name }))) remove.mutate(s.id);
                      }}
                    >
                      <Trash2 aria-hidden />
                    </Button>
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>
      {editing ? <SupplierDialog supplier={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
      {importing ? <ImportSuppliersDialog onClose={() => setImporting(false)} /> : null}
    </div>
  );
}

export function SupplierDialog({
  supplier,
  initial,
  onClose,
  onSaved,
}: {
  supplier: Supplier | null;
  /** ספק חדש עם פרטים ממולאים מראש (למשל מקובץ חוזה). */
  initial?: Partial<SupplierInput>;
  onClose: () => void;
  onSaved?: (s: Supplier) => void;
}) {
  const { t } = useTranslation(['suppliers', 'common']);
  const toast = useToast();
  const qc = useQueryClient();
  const [v, setV] = useState<SupplierInput>({
    name: supplier?.name ?? initial?.name ?? '',
    contactName: supplier?.contactName ?? initial?.contactName ?? '',
    phone: supplier?.phone ?? initial?.phone ?? '',
    email: supplier?.email ?? initial?.email ?? '',
    taxId: supplier?.taxId ?? initial?.taxId ?? '',
    notes: supplier?.notes ?? initial?.notes ?? '',
  });
  const emailInvalid = !!v.email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email.trim());
  const save = useMutation({
    mutationFn: () => {
      const body = Object.fromEntries(Object.entries(v).map(([k, x]) => [k, typeof x === 'string' ? x.trim() : x])) as SupplierInput;
      // שדה ריק ביצירה — לא נשלח (האימייל נבדק בשרת).
      if (!supplier) for (const k of Object.keys(body) as Array<keyof SupplierInput>) if (!body[k]) delete body[k];
      return supplier ? suppliersApi.update(supplier.id, body) : suppliersApi.create(body);
    },
    onSuccess: async (s) => {
      toast.success(supplier ? t('dialog.updated') : t('dialog.added'));
      await qc.invalidateQueries({ queryKey: SUPPLIERS_KEY });
      onSaved?.(s);
      onClose();
    },
    onError: (e) => toast.error(t('common:errors.saveFailed'), e instanceof ApiError ? e.message : undefined),
  });
  const set = (k: keyof SupplierInput) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setV({ ...v, [k]: e.target.value });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent aria-describedby="supplier-desc">
        <DialogHeader>
          <DialogTitle>{supplier ? t('dialog.editTitle', { name: supplier.name }) : t('dialog.newTitle')}</DialogTitle>
          <DialogDescription id="supplier-desc">{t('dialog.description')}</DialogDescription>
        </DialogHeader>
        <DialogBody className="grid gap-3 sm:grid-cols-2">
          <Field label={t('dialog.name')} htmlFor="sp-name" required>
            <Input id="sp-name" autoFocus maxLength={200} value={v.name} onChange={set('name')} />
          </Field>
          <Field label={t('dialog.contact')} htmlFor="sp-contact">
            <Input id="sp-contact" maxLength={120} value={v.contactName} onChange={set('contactName')} />
          </Field>
          <Field label={t('dialog.phone')} htmlFor="sp-phone">
            <Input id="sp-phone" type="tel" dir="ltr" maxLength={40} value={v.phone} onChange={set('phone')} />
          </Field>
          <Field label={t('dialog.email')} htmlFor="sp-email" error={emailInvalid ? t('common:errors.invalidEmail') : undefined}>
            <Input id="sp-email" type="email" dir="ltr" maxLength={320} value={v.email} onChange={set('email')} />
          </Field>
          <Field label={t('dialog.taxId')} htmlFor="sp-tax">
            <Input id="sp-tax" dir="ltr" inputMode="numeric" maxLength={20} value={v.taxId} onChange={set('taxId')} />
          </Field>
          <Field label={t('dialog.notes')} htmlFor="sp-notes" className="sm:col-span-2">
            <Textarea id="sp-notes" rows={2} maxLength={2000} value={v.notes} onChange={set('notes')} />
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button disabled={!v.name.trim() || emailInvalid} loading={save.isPending} onClick={() => save.mutate()}>
            {t('common:actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
